import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { ArrowRight, X, Loader2, Star, Quote, Check, MessageSquare, Clock, Sparkles, ShieldCheck, UserCheck } from "lucide-react";

export default function Landing() {
  const { user, loading, setUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [authOpen, setAuthOpen] = useState(false);
  const [mode, setMode] = useState("register"); // "register" | "login"
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [plansMap, setPlansMap] = useState({});
  const [hasActivePlan, setHasActivePlan] = useState(true);
  const [weeksPerCycle, setWeeksPerCycle] = useState(4);
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    api.get("/public/testimonials").then((r) => setTestimonials(r.data.testimonials || [])).catch(() => {});
    api.get("/plans").then((r) => {
      const m = {};
      (r.data.plans || []).forEach((p) => { m[p.key] = p; });
      setPlansMap(m);
      setHasActivePlan(!!r.data.has_active_plan);
      if (r.data.weeks_per_cycle) setWeeksPerCycle(r.data.weeks_per_cycle);
    }).catch(() => {});
    api.get("/welcome/public").then((r) => setCampaign(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("ref_code", ref.trim().toUpperCase());
  }, []);

  useEffect(() => {
    if (!loading && user) navigate(user.role === "coach" ? "/coach" : "/app", { replace: true });
  }, [user, loading, navigate]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const openAuth = (m = "register") => { setMode(m); setErr(""); setAuthOpen(true); };

  const submitLocal = async (e) => {
    e.preventDefault();
    setErr(""); setMsg("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await api.post("/auth/forgot-password", { email: form.email });
        setMsg(res.data?.message || "Si un compte existe, un email vient de partir.");
        return;
      }
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register"
        ? { name: form.name, email: form.email, password: form.password }
        : { email: form.email, password: form.password };
      const res = await api.post(path, payload);
      const ref = localStorage.getItem("ref_code");
      if (ref) { try { await api.post("/referral/attach", { code: ref }); } catch {} localStorage.removeItem("ref_code"); }
      setUser(res.data);
      navigate(res.data.role === "coach" ? "/coach" : "/app", { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Une erreur est survenue. Réessaie.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-sand/80 border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
        <img src="/logo.png" alt="MAIRUNCOACH" className="h-7 w-auto" data-testid="brand-logo" />
        <div className="flex items-center gap-4">
          <button data-testid="header-login-button" onClick={() => openAuth("login")} className="text-sm font-medium hover:text-sage transition-[color]">{t("login")}</button>
        </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-8 sm:pt-14 pb-24">
        {/* 1. HERO */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2.5 text-sm text-obsidian/55 mb-7">
              <span className="relative flex h-2 w-2">
                <span className="pulse-organic absolute inline-flex h-full w-full rounded-full bg-sage opacity-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-sage" />
              </span>
              <span className="tracking-wide">Programme running personnalisé · IA transparente</span>
            </div>
            <h1 className="font-display text-4xl sm:text-6xl font-light tracking-tight leading-[1.03]">
              Ton programme running,<br /><span className="text-sage">construit à partir de toi.</span>
            </h1>
            <p className="mt-7 text-lg text-obsidian/70 leading-relaxed max-w-xl">
              MAI analyse ton niveau, ton objectif et le temps dont tu disposes pour créer ton cycle de {weeksPerCycle} semaines et t'accompagner séance après séance.
            </p>

            <div className="mt-9 flex flex-col items-start gap-3">
              <button data-testid="start-bilan-button" onClick={() => navigate("/decouverte")}
                className="group inline-flex items-center gap-3 rounded-full bg-obsidian text-white px-7 py-4 text-base font-medium hover:bg-obsidian/90 transition-[transform,background-color] hover:-translate-y-0.5">
                Faire mon bilan et voir mon programme
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-sage">
                <ShieldCheck size={16} /> Gratuit · Sans carte bancaire · Sans engagement
              </span>
              <span className="text-sm text-obsidian/50">Aucun paiement automatique. Tu décides ensuite si tu souhaites poursuivre.</span>
            </div>
          </div>

          {/* Composition : une vraie semaine MAI (preuve produit) sur photo running en fond */}
          <div className="fade-up relative" style={{ animationDelay: "0.1s" }}>
            <div className="relative rounded-[28px] overflow-hidden p-5 sm:p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.4)]">
              <img src="https://images.unsplash.com/photo-1558659976-f411e3af62de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Coureuse au lever du soleil" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-obsidian/55 backdrop-blur-[2px]" />
              <div className="relative">
                <div className="text-[11px] tracking-[0.18em] uppercase text-white/70 font-semibold">Ta semaine avec MAI</div>
                <div className="mt-3 space-y-2.5" data-testid="hero-week-preview">
                  {[
                    { n: 1, t: "Endurance fondamentale", d: "40 min" },
                    { n: 2, t: "Fractionné", d: "45 min" },
                    { n: 3, t: "Sortie longue", d: "1 h 10" },
                  ].map((s) => (
                    <div key={s.n} data-testid={`hero-session-${s.n}`} className="flex items-center gap-3 rounded-2xl backdrop-blur-xl bg-white/90 border border-white/60 px-4 py-3">
                      <span className="w-7 h-7 shrink-0 rounded-full bg-sage/15 text-sage text-sm font-semibold flex items-center justify-center">{s.n}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{s.t}</div>
                      </div>
                      <div className="inline-flex items-center gap-1 text-xs text-obsidian/60"><Clock size={13} /> {s.d}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2. COMMENT ÇA FONCTIONNE */}
        <div className="mt-20 sm:mt-28">
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight text-center">De ton bilan à ta première séance</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { n: 1, t: "Fais ton bilan", d: "Indique ton niveau, ton objectif, ta fréquence et le temps que tu peux consacrer à tes séances." },
              { n: 2, t: "Découvre ton programme", d: "MAI construit ton cycle et te présente tes semaines et tes séances." },
              { n: 3, t: "Progresse avec MAI", d: "Pose tes questions depuis chaque séance et partage tes sensations pour préparer la suite." },
            ].map((s) => (
              <div key={s.n} data-testid={`how-step-${s.n}`} className="rounded-2xl border border-border bg-white p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-premium-hover">
                <span className="inline-flex w-10 h-10 rounded-full bg-sage/10 text-sage text-lg font-semibold items-center justify-center">{s.n}</span>
                <h3 className="mt-4 text-lg font-medium">{s.t}</h3>
                <p className="mt-2 text-sm text-obsidian/60 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-obsidian/55">Ton bilan et l'accès à ton programme se font sans carte bancaire.</p>
        </div>

        {/* 3. MAI EN ACTION */}
        <div className="mt-20 sm:mt-28 rounded-[28px] bg-obsidian text-white p-8 sm:p-12">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.2em] uppercase text-sage font-semibold">MAI en action</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light tracking-tight leading-tight">Une question sur ta séance ? Demande à MAI.</h2>
          </div>

          <div className="mt-8 max-w-2xl space-y-3" data-testid="mai-demo-conversation">
            <div className="flex justify-end">
              <div className="rounded-2xl rounded-br-md bg-sage/20 border border-sage/30 px-4 py-3 text-sm max-w-[85%]">Je n'ai que 30 minutes aujourd'hui.</div>
            </div>
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-md bg-white/[0.06] border border-white/10 px-4 py-3 text-sm text-white/85 max-w-[90%]">
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-sage font-semibold mb-1.5"><Sparkles size={12} /> MAI</span>
                <p>On garde l'essentiel : 8 minutes faciles, 6 × 1 minute soutenue avec 1 minute de récupération, puis 6 minutes de retour au calme. Total : 26 minutes.</p>
                <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px] text-white/70">
                  <ShieldCheck size={12} /> Ajustement proposé — aucun changement enregistré
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2" data-testid="mai-demo-examples">
            {["Je suis à l'hôtel", "Cette séance était trop difficile", "Quand dois-je faire cette séance ?"].map((q) => (
              <span key={q} className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] border border-white/10 px-3.5 py-2 text-sm text-white/70">
                <MessageSquare size={13} className="text-sage" /> {q}
              </span>
            ))}
          </div>

          <p className="mt-6 text-sm text-white/55 max-w-2xl leading-relaxed">
            MAI peut expliquer, conseiller et proposer un ajustement ponctuel. Ton programme n'est modifié qu'après confirmation et enregistrement réel.
          </p>
        </div>

        {/* 4. LES DEUX OFFRES */}
        <div className="mt-20 sm:mt-28">
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight text-center">Deux façons d'avancer</h2>
          <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            {/* Essentiel */}
            <div data-testid="offer-card-essentiel" className="relative rounded-[24px] border border-border bg-white p-7 flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-premium-hover">
              {campaign?.campaign_active && (
                <span data-testid="offer-campaign-badge" className="absolute -top-3 left-7 inline-flex items-center gap-1.5 rounded-full bg-sage text-white px-3 py-1 text-xs font-semibold shadow">
                  <Sparkles size={12} /> 1er cycle offert
                </span>
              )}
              <h3 className="text-xl font-medium">{plansMap.essentiel?.label || "Essentiel"}</h3>
              <p className="mt-2 text-sm text-obsidian/60 leading-relaxed">Ton programme personnalisé et MAI pour t'accompagner séance après séance.</p>
              {hasActivePlan && plansMap.essentiel?.price_period_display && (
                <div data-testid="offer-price-essentiel" className="mt-4 text-2xl font-light">{plansMap.essentiel.price_period_display}</div>
              )}
              <ul className="mt-5 space-y-2.5 text-sm text-obsidian/70 flex-1">
                {[`Cycle de ${weeksPerCycle} semaines`, "Programme personnalisé", "Accès à MAI", "Questions techniques", "Conseils", "Ajustements ponctuels"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check size={16} className="text-sage mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-obsidian/45">Les refontes complètes ne sont pas comprises.</p>
            </div>

            {/* Premium */}
            <div data-testid="offer-card-premium" className="rounded-[24px] border border-obsidian bg-obsidian text-white p-7 flex flex-col transition-transform duration-300 hover:-translate-y-1">
              <h3 className="text-xl font-medium flex items-center gap-2"><UserCheck size={18} className="text-sage" /> {plansMap.premium?.label || "Premium"}</h3>
              <p className="mt-2 text-sm text-white/65 leading-relaxed">MAI avec le recul et le suivi d'un coach humain.</p>
              {hasActivePlan && plansMap.premium?.price_period_display && (
                <div data-testid="offer-price-premium" className="mt-4 text-2xl font-light">{plansMap.premium.price_period_display}</div>
              )}
              <ul className="mt-5 space-y-2.5 text-sm text-white/75 flex-1">
                {["Tout Essentiel", "Un rendez-vous prévu dans l'offre", "Suivi par messages dans les limites de l'application", "Analyse humaine", "Validation des changements importants"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check size={16} className="text-sage mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {testimonials.length > 0 && (
          <div className="mt-20 sm:mt-28" data-testid="testimonials-section">
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-[11px] tracking-[0.2em] uppercase text-sage font-semibold">Ils ont commencé par leur bilan</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light tracking-tight">Ce que disent les coureurs</h2>
              <p className="mt-3 text-sm text-obsidian/55">Des retours de coureurs qui utilisent MAI au quotidien.</p>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.slice(0, 6).map((tm, i) => (
                <div key={i} data-testid={`testimonial-${i}`} className="relative rounded-2xl border border-border bg-white p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-premium-hover">
                  <Quote size={26} className="text-sage/25" />
                  <div className="mt-2 flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={15} strokeWidth={1.6} className={n <= (tm.rating || 0) ? "fill-amber-400 text-amber-400" : "text-obsidian/15"} />
                    ))}
                  </div>
                  {tm.comment && <p className="mt-3 text-sm text-obsidian/70 leading-relaxed">« {tm.comment} »</p>}
                  <div className="mt-4 text-sm font-medium text-obsidian/80">{tm.author}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <button data-testid="testimonials-cta" onClick={() => navigate("/decouverte")}
                className="inline-flex items-center gap-2 rounded-full border border-obsidian/20 px-6 py-3 text-sm font-medium hover:bg-obsidian/[0.03] transition-colors">
                Faire mon bilan à mon tour <ArrowRight size={15} />
              </button>
            </div>
          </div>
        )}

        {/* 5. CTA FINAL */}
        <div className="mt-20 sm:mt-28 rounded-[28px] border border-border bg-white p-8 sm:p-12 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tight max-w-2xl mx-auto">
            Commence par ton bilan. <span className="text-sage">Regarde d'abord ce que MAI construit pour toi.</span>
          </h2>
          <p className="mt-3 text-obsidian/60 max-w-xl mx-auto">Quelques minutes suffisent pour découvrir ton programme personnalisé.</p>
          <button data-testid="bottom-cta" onClick={() => navigate("/decouverte")}
            className="mt-7 inline-flex items-center gap-3 rounded-full bg-obsidian text-white px-7 py-4 font-medium hover:-translate-y-0.5 transition-transform">
            Faire mon bilan et voir mon programme <ArrowRight size={18} />
          </button>
          <div className="mt-3 text-sm font-semibold text-sage">Gratuit · Sans carte bancaire · Sans engagement</div>
          <div className="mt-1 text-sm text-obsidian/45">Aucun paiement automatique.</div>
        </div>
      </section>

      {authOpen && (
        <div data-testid="auth-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-obsidian/50 backdrop-blur-sm" onClick={() => !busy && setAuthOpen(false)} />
          <div className="relative w-full max-w-md rounded-[24px] bg-white shadow-[0_40px_100px_-30px_rgba(0,0,0,0.5)] border border-border/60 p-7 sm:p-8 fade-up">
            <button data-testid="auth-close" onClick={() => setAuthOpen(false)} className="absolute top-4 right-4 p-1.5 rounded-full text-obsidian/40 hover:text-obsidian hover:bg-obsidian/5 transition-colors"><X size={18} /></button>

            <h2 className="font-display text-2xl font-light tracking-tight">
              {mode === "register" ? "Créer mon compte" : mode === "forgot" ? "Mot de passe oublié" : "Se connecter"}
            </h2>
            <p className="mt-1.5 text-sm text-obsidian/55">
              {mode === "register" ? "Commence ton bilan running gratuitement." : mode === "forgot" ? "Entre ton email : on t'envoie un lien pour choisir un nouveau mot de passe." : "Content de te revoir."}
            </p>

            {mode !== "forgot" && (<>
            <button data-testid="auth-google-button" onClick={login}
              className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-full border border-border bg-white px-6 py-3 text-sm font-medium hover:bg-obsidian/[0.03] transition-colors">
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
              Continuer avec Google
            </button>

            <div className="my-5 flex items-center gap-3 text-xs text-obsidian/40">
              <span className="h-px flex-1 bg-border" /> ou avec ton email <span className="h-px flex-1 bg-border" />
            </div>
            </>)}

            <form onSubmit={submitLocal} className="space-y-3">
              {mode === "register" && (
                <input data-testid="auth-name-input" type="text" placeholder="Prénom" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 transition-[border-color,box-shadow]" />
              )}
              <input data-testid="auth-email-input" type="email" required placeholder="Adresse email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 transition-[border-color,box-shadow]" />
              {mode !== "forgot" && (
              <input data-testid="auth-password-input" type="password" required placeholder="Mot de passe (min. 6 caractères)" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-sage focus:ring-2 focus:ring-sage/20 transition-[border-color,box-shadow]" />
              )}

              {mode !== "register" && mode !== "forgot" && (
                <div className="text-right">
                  <button type="button" data-testid="auth-forgot-link" onClick={() => { setErr(""); setMsg(""); setMode("forgot"); }}
                    className="text-xs text-obsidian/50 hover:text-sage hover:underline">Mot de passe oublié ?</button>
                </div>
              )}

              {err && <p data-testid="auth-error" className="text-sm text-destructive">{err}</p>}
              {msg && <p data-testid="auth-message" className="text-sm text-sage">{msg}</p>}

              <button data-testid="auth-submit-button" type="submit" disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-obsidian text-white px-6 py-3 text-sm font-medium hover:bg-obsidian/90 transition-colors disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />}
                {mode === "register" ? "Créer mon compte" : mode === "forgot" ? "Envoyer le lien" : "Se connecter"}
              </button>
            </form>

            <p className="mt-5 text-center text-sm text-obsidian/55">
              {mode === "forgot" ? (
                <button data-testid="auth-back-login" onClick={() => { setErr(""); setMsg(""); setMode("login"); }}
                  className="text-sage font-medium hover:underline">Retour à la connexion</button>
              ) : (<>
                {mode === "register" ? "Déjà un compte ?" : "Pas encore de compte ?"}{" "}
                <button data-testid="auth-toggle-mode" onClick={() => { setErr(""); setMsg(""); setMode(mode === "register" ? "login" : "register"); }}
                  className="text-sage font-medium hover:underline">
                  {mode === "register" ? "Se connecter" : "Créer un compte"}
                </button>
              </>)}
            </p>
          </div>
        </div>
      )}

      <footer data-testid="app-footer" className="border-t border-white/10 bg-obsidian text-white/70">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/logo-white.png" alt="MAIRUNCOACH" className="h-6 w-auto opacity-90" />
          <div className="text-sm text-center sm:text-right">
            <div>Une question ?{" "}
              <button data-testid="footer-support-chat" onClick={() => navigate("/support")}
                className="text-sage font-medium hover:underline">Discuter avec l'assistant support</button>
              {" "}ou écris-nous à{" "}
              <a data-testid="footer-support-email" href="mailto:supportmairuncoach@gmail.com"
                className="text-sage font-medium hover:underline">supportmairuncoach@gmail.com</a>
            </div>
            <div className="mt-1 text-xs text-white/40">© {new Date().getFullYear()} MAIRUNCOACH. Coaching running IA + humain.</div>
            <div className="mt-1 text-xs">
              <button data-testid="footer-cgu" onClick={() => navigate("/cgu")} className="text-white/50 hover:text-white hover:underline">Conditions Générales d'Utilisation</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
